/**
 * Platform & in-app-webview detection (Onda First-60s, front B) — the social-traffic door.
 *
 * A large share of Sea of Solana's traffic arrives through an in-app browser (X/Twitter, Telegram, Discord,
 * Instagram, Facebook, TikTok, …). Some of those embedded webviews cannot create a WebGL2 context, so the game
 * would render a black screen — a silent bounce. These PURE helpers classify the user agent so the boot code can
 * (a) show an "Open in your browser" escape hatch when a webview also lacks WebGL2, and (b) show a discreet,
 * dismissible "you'll get the best experience in your browser" banner when a webview DOES render fine.
 *
 * Design rule: ZERO false-blocks. Detection only ever downgrades to a friendlier screen or a dismissible banner —
 * it NEVER prevents a capable browser from playing. When unsure, we return `webview:false` and let them sail.
 *
 * All functions here are pure (UA string in, verdict out) so they unit-test with no DOM. The DOM/runtime glue
 * (reading navigator.userAgent, the WebGL2 probe) lives in main.ts.
 */

export type MobileOs = 'android' | 'ios' | 'other';

/** Coarse mobile-OS classification from a UA string. iPadOS masquerades as desktop Safari, so a Macintosh UA with
 *  touch (passed via `touch`) is treated as iOS. Pure. */
export function mobileOs(ua: string, opts: { touch?: boolean } = {}): MobileOs {
  const s = ua || '';
  if (/android/i.test(s)) return 'android';
  if (/iphone|ipod|ipad/i.test(s)) return 'ios';
  if (/macintosh/i.test(s) && opts.touch) return 'ios'; // iPadOS 13+ desktop-class Safari
  return 'other';
}

export interface WebviewVerdict {
  /** True when the page is very likely running inside an app's embedded browser (not a real browser). */
  webview: boolean;
  /** A human-friendly source name for the copy ('X', 'Instagram', 'in-app browser', …); '' when not a webview. */
  app: string;
}

/** Named in-app browsers, matched against the UA. Order matters only for the label (first hit wins). */
const NAMED_WEBVIEWS: [RegExp, string][] = [
  [/\bFBAN\/|\bFBAV\/|\bFB_IAB\b|\bFBIOS\b|\bFB4A\b/i, 'Facebook'],
  [/\bInstagram\b/i, 'Instagram'],
  [/\bMessenger\b|\bMessengerLite\b/i, 'Messenger'],
  [/\b(?:musical_ly|BytedanceWebview|TikTok|Bytedance|trill)\b/i, 'TikTok'],
  [/\bTwitter\b|\bTwitterAndroid\b/i, 'X'],
  [/\bSnapchat\b/i, 'Snapchat'],
  [/\bLine\//i, 'LINE'],
  [/\bPinterest\b/i, 'Pinterest'],
  [/\bLinkedInApp\b/i, 'LinkedIn'],
  [/\bDiscord\b/i, 'Discord'],
  [/\bKAKAOTALK\b/i, 'KakaoTalk'],
  [/\bWhatsApp\b/i, 'WhatsApp'],
];

/**
 * Classify whether the UA is an in-app webview. PURE.
 *  - Named apps (Facebook/Instagram/TikTok/X/…) are matched directly on their UA token.
 *  - Generic Android WebView carries the `; wv)` marker.
 *  - Generic iOS WKWebView is the tricky one: an in-app WKWebView UA has AppleWebKit but LACKS the `Safari` token
 *    AND lacks any alt-browser token (CriOS/FxiOS/EdgiOS/…). A standalone-PWA also lacks `Safari`, so callers pass
 *    `standalone` to exclude it (a PWA is not a webview trap).
 * `opts.touch` helps place iPadOS; `opts.standalone` is navigator.standalone / display-mode:standalone.
 */
export function detectWebview(ua: string, opts: { standalone?: boolean; touch?: boolean } = {}): WebviewVerdict {
  const s = ua || '';
  if (!s) return { webview: false, app: '' };
  for (const [re, name] of NAMED_WEBVIEWS) if (re.test(s)) return { webview: true, app: name };

  // Generic Android system WebView.
  if (/;\s*wv\)/i.test(s) || /\bwv\b.*Version\/[\d.]+/i.test(s)) return { webview: true, app: 'in-app browser' };

  // Generic iOS WKWebView: iOS-family UA, not a standalone PWA, no Safari token, no alt-browser token.
  const os = mobileOs(s, { touch: opts.touch });
  if (os === 'ios' && !opts.standalone) {
    const altBrowser = /(CriOS|FxiOS|EdgiOS|OPiOS|GSA)\//i.test(s); // Chrome/Firefox/Edge/Opera/Google on iOS = real browsers
    const realSafari = /Safari/i.test(s) && !altBrowser;
    if (!realSafari && !altBrowser && /AppleWebKit/i.test(s)) return { webview: true, app: 'in-app browser' };
  }
  return { webview: false, app: '' };
}

export interface WalletVerdict {
  /** True when the page is very likely running inside a crypto-wallet's in-app browser (Phantom / MetaMask / …). */
  wallet: boolean;
  /** Human-friendly wallet name for the copy ('Phantom', 'MetaMask', …); '' when not a wallet browser. */
  app: string;
}

/** Signals read from the injected web3 providers (DOM glue reads these in main.ts and passes them in — the pure
 *  helper never touches `window`). A wallet in-app browser injects its provider globally; combined with a webview
 *  context they identify the wallet even when its UA token is generic. */
export interface WalletProviders {
  phantom?: boolean;      // window.phantom present (Phantom's Solana wallet)
  solana?: boolean;       // window.solana present (Phantom / Solflare inject a Solana provider)
  ethereum?: boolean;     // window.ethereum present (any EVM wallet)
  isMetaMask?: boolean;   // window.ethereum.isMetaMask
  isCoinbase?: boolean;   // window.ethereum.isCoinbaseWallet
  isTrust?: boolean;      // window.ethereum.isTrust / window.trustwallet
  isRainbow?: boolean;    // window.ethereum.isRainbow
  isPhantomEth?: boolean; // window.ethereum.isPhantom (Phantom's EVM provider)
}

/** Named crypto-wallet in-app browsers, matched on their UA token. First hit wins (label only). Deliberately
 *  tight tokens (no bare "Trust"/"Rainbow" words that could match unrelated apps' UAs) — the provider path below
 *  catches the versions that ship a generic WKWebView UA. */
const WALLET_WEBVIEWS: [RegExp, string][] = [
  [/\bPhantom\b/i, 'Phantom'],
  [/\bMetaMaskMobile\b|\bMetaMask\b/i, 'MetaMask'],
  [/\bCoinbaseWallet\b|\bCoinbaseBrowser\b/i, 'Coinbase Wallet'],
  [/\bTrustWallet\b|\bTrust\/[\d.]/i, 'Trust'],
  [/\bRainbow\b/i, 'Rainbow'],
];

/**
 * Classify whether the page is running inside a crypto-wallet's in-app browser. PURE.
 *  - A wallet UA token (Phantom / MetaMaskMobile / CoinbaseWallet / …) is the strongest, self-labelling tell.
 *  - Failing that, the injected web3 provider (`window.phantom` / `window.solana` / `window.ethereum` flags) — but
 *    ONLY when the page is also an in-app webview (or an iOS non-standalone AppleWebKit context). A desktop
 *    browser EXTENSION (MetaMask/Phantom) injects the same globals, and there we must NOT fire (it's a capable
 *    real browser). Gating on the webview context keeps the zero-false-block promise for desktop extension users.
 *
 * This is a PERFORMANCE hint (wallet webviews carry a much smaller memory ceiling than Safari), never a block —
 * callers only ever downgrade to light-mode or a dismissible banner. When unsure, returns `wallet:false`.
 */
export function detectWalletBrowser(
  ua: string,
  opts: { providers?: WalletProviders; standalone?: boolean; touch?: boolean } = {},
): WalletVerdict {
  const s = ua || '';
  for (const [re, name] of WALLET_WEBVIEWS) if (re.test(s)) return { wallet: true, app: name };

  const p = opts.providers;
  if (p) {
    // Only trust the injected-provider signal inside a webview (or a bare iOS WKWebView context). This excludes
    // desktop/mobile browsers running a wallet EXTENSION, which are perfectly capable and must keep sailing.
    const inWebview = detectWebview(s, { standalone: opts.standalone, touch: opts.touch }).webview
      || (mobileOs(s, { touch: opts.touch }) === 'ios' && !opts.standalone);
    if (inWebview) {
      if (p.isMetaMask) return { wallet: true, app: 'MetaMask' };
      if (p.isCoinbase) return { wallet: true, app: 'Coinbase Wallet' };
      if (p.isTrust) return { wallet: true, app: 'Trust' };
      if (p.isRainbow) return { wallet: true, app: 'Rainbow' };
      if (p.phantom || p.isPhantomEth || p.solana) return { wallet: true, app: 'Phantom' };
      if (p.ethereum) return { wallet: true, app: 'your wallet' };
    }
  }
  return { wallet: false, app: '' };
}

/**
 * Decide whether to run the memory-frugal LIGHT MODE. PURE (no DOM). Light mode caps the device-pixel-ratio,
 * drops bloom/decorative density and disposes the sea harder on the way ashore so the walkable port fits under a
 * tight WKWebView memory ceiling. It NEVER touches desktop — every branch requires a wallet webview, an iOS
 * in-app webview, or a low-RAM phone.
 */
export function lightModeDecision(opts: { walletWebview?: boolean; webview?: boolean; deviceMemory?: number; os?: MobileOs }): boolean {
  if (opts.walletWebview) return true;                    // wallet in-app browser = the tightest memory ceiling
  if (opts.webview && opts.os === 'ios') return true;     // any iOS WKWebview shares the small-heap OOM trap
  const dm = opts.deviceMemory;                            // navigator.deviceMemory (Chrome/Android; absent on iOS)
  if (typeof dm === 'number' && dm > 0 && dm <= 4 && (opts.os === 'ios' || opts.os === 'android')) return true;
  return false;
}

/**
 * Build an Android `intent://` URL that forces the current page to open in the system browser (Chrome), escaping
 * the in-app webview. Uses a `browser_fallback_url` so it degrades gracefully if Chrome isn't installed. Returns
 * '' when `href` can't be parsed. Pure. (There is NO reliable programmatic escape on iOS — callers instruct the
 * user to tap the webview's ⋯ / "Aa" menu → "Open in Browser" and offer a copy-link button instead.)
 */
export function androidIntentUrl(href: string): string {
  try {
    const u = new URL(href);
    const scheme = u.protocol.replace(/:$/, '') || 'https';
    const path = `${u.pathname}${u.search}${u.hash}`;
    return `intent://${u.host}${path}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
  } catch { return ''; }
}
